/* eslint-disable formatjs/no-literal-string-in-jsx */
import React from 'react'
import { ClassNames } from '@emotion/react'
import classNames from 'classnames'

import { Button } from '@slite/components'
import { ArrowRightIcon } from '@slite/icons'

// Button's inner <div> carries the size's padding-horizontal utility; the
// outer <button> only carries border + min-height. We override the inner div
// from an outer wrapper since Button doesn't forward `css`/inner classNames.
const EXTRA_PADDING = {
  '& > button > div': { paddingLeft: 8, paddingRight: 8 },
}

export type DiffShape =
  | { kind: 'replace'; before: string; after: string }
  | { kind: 'add'; text: string }
  | { kind: 'remove'; text: string }

const PILL_STYLES = {
  remove: {
    background: '#fdecec',
    color: '#991b1b',
    textDecoration: 'line-through' as const,
  },
  add: { background: '#e6f6ec', color: '#166534' },
}

const DiffPill = ({ text, kind }: { text: string; kind: 'add' | 'remove' }) => (
  <span
    className={classNames(
      'radius-large flexbox-no-shrink',
      kind === 'add' && 'font-weight-semibold'
    )}
    style={{ padding: '4px 10px', fontSize: 14, ...PILL_STYLES[kind] }}
  >
    {text}
  </span>
)

const DiffPreview = ({ diff }: { diff: DiffShape }) => {
  switch (diff.kind) {
    case 'replace':
      return (
        <>
          <DiffPill text={diff.before} kind="remove" />
          <ArrowRightIcon size={14} className="shade-30 flexbox-no-shrink" />
          <DiffPill text={diff.after} kind="add" />
        </>
      )
    case 'add':
      return <DiffPill text={`+ ${diff.text}`} kind="add" />
    case 'remove':
      return <DiffPill text={diff.text} kind="remove" />
  }
}

// Mirrors packages/screens/src/SliteAgent/ApprovalCard.
export const DiffCard = ({
  diff,
  approveLabel = 'Approve',
  rejectLabel = 'Reject',
}: {
  diff: DiffShape
  approveLabel?: string
  rejectLabel?: string
}) => (
  <div className="flexbox flexbox-column flexbox-gap-8">
    <div
      className={classNames(
        'flexbox flexbox-centered flexbox-gap-8',
        'border-small border-shade-45 radius-large',
        'padding-vertical-8 padding-horizontal-8'
      )}
      style={{ background: 'rgba(52, 52, 52, 0.01)' }}
    >
      <DiffPreview diff={diff} />
    </div>
    <ClassNames>
      {({ css }) => (
        <div className="flexbox flexbox-align-center flexbox-justify-end flexbox-gap-8">
          <div
            className={classNames('margin-left-auto', css(EXTRA_PADDING))}
            style={{ display: 'inline-flex' }}
          >
            <Button variant="secondary" size="smaller">
              {rejectLabel}
            </Button>
          </div>
          {/* Wrapper carries the data attribute so the story cursor can scale-press it. */}
          <div
            data-agent-apply
            className={css(EXTRA_PADDING)}
            style={{
              display: 'inline-flex',
              transition: 'transform 0.12s ease',
            }}
          >
            <Button variant="secondary-purple" size="smaller">
              {approveLabel}
            </Button>
          </div>
        </div>
      )}
    </ClassNames>
  </div>
)
